import React, {useEffect, useRef, useState, useCallback} from 'react';
import {GoogleMap, Marker, useJsApiLoader} from '@react-google-maps/api';
import Lottie from 'react-lottie';
import animation from '../../assets/loadingLocation.json';
import HelperGeneral from "../../helpers/HelperGeneral";

const containerStyle = {
    width: '100%',
    height: '400px',
}

const DEFAULT_CENTER = {lat: 41.0509277, lng: 28.8949435}

const GOOGLE_MAPS_LIBRARIES = ['places', 'geometry']

function toLatLngLiteral(marker) {
    if (!marker) return null
    const lat = Number(marker.latitude)
    const lng = Number(marker.longitude)
    if (!isFinite(lat) || !isFinite(lng)) return null
    return {lat, lng}
}

const GoogleMaps = ({markers = [], defaultZoom = 11, currentLocation, coords: setCoordsProp}) => {
    const mapRef = useRef(null)
    const [userCoords, setUserCoords] = useState(null)
    const [loadingGeo, setLoadingGeo] = useState(false)

    const {isLoaded, loadError} = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyCWggT6n6ma7hGTobdUY25YT8LEmlaZcBArou',
        libraries: GOOGLE_MAPS_LIBRARIES,
    })

    useEffect(() => {
        if (markers.length === 0 && typeof currentLocation !== 'undefined') {
            setLoadingGeo(true);
            if (navigator.geolocation) {
                navigator.geolocation.getCurrentPosition(
                    (position) => {
                        const lat = Number(position.coords.latitude);
                        const lng = Number(position.coords.longitude);
                        if (isFinite(lat) && isFinite(lng)) {
                            setUserCoords({lat, lng});
                            if (typeof setCoordsProp === 'function') setCoordsProp(position.coords)
                        }
                        setLoadingGeo(false);
                    },
                    () => setLoadingGeo(false)
                )
            } else {
                setLoadingGeo(false);
            }
        }
    }, [markers.length, currentLocation, setCoordsProp])

    const fitMapToMarkers = useCallback((map, markersArr, userCoordsLocal) => {
        if (!map) return
        const positions = markersArr?.map(toLatLngLiteral)?.filter(Boolean);

        if (positions.length === 0) {
            const center = userCoordsLocal || DEFAULT_CENTER
            map.setCenter(center)
            map.setZoom(defaultZoom)
            return
        }

        if (positions.length === 1) {
            map.setCenter(positions[0])
            map.setZoom(defaultZoom)
            return
        }

        const bounds = new window.google.maps.LatLngBounds()
        positions.forEach(p => bounds.extend(p))
        map.fitBounds(bounds)
    }, [defaultZoom])

    const onLoad = useCallback((map) => {
        mapRef.current = map
        fitMapToMarkers(map, markers, userCoords)
    }, [fitMapToMarkers, markers, userCoords])

    const onUnmount = useCallback(() => {
        mapRef.current = null
    }, [])

    useEffect(() => {
        if (!isLoaded) return
        if (mapRef.current) fitMapToMarkers(mapRef.current, markers, userCoords)
    }, [isLoaded, markers, userCoords, fitMapToMarkers])

    if (loadError) return <div>Google Maps failed to load</div>
    if (!isLoaded) return <div>Loading maps…</div>

    const defaultOptions = {
        loop: true,
        autoplay: true,
        animationData: animation,
        rendererSettings: {preserveAspectRatio: 'xMidYMid slice'},
    }

    return (
        <GoogleMap
            mapContainerStyle={containerStyle}
            center={userCoords || DEFAULT_CENTER}
            zoom={defaultZoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
        >
            {
                markers?.map((marker, index) => {
                    const pos = toLatLngLiteral(marker)
                    if (!pos) return null
                    return (
                        <Marker
                            key={marker.id ?? `${pos.lat}-${pos.lng}`}
                            position={pos}
                            label={{
                                text: String(index + 1),
                                color: "white",
                                fontSize: "16px",
                                fontWeight: "400",
                                fontFamily: 'Jost'
                            }}
                            onClick={() => HelperGeneral.scrollToClassName(`.pharmacy-${index + 1}`)}
                        />
                    )
                })
            }
            {
                loadingGeo && (
                    <div
                        style={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 9999,
                            background: 'rgba(255,255,255,0.7)',
                            borderRadius: 12,
                            padding: 8,
                        }}
                    >
                        <Lottie options={defaultOptions} height={100} width={68}/>
                    </div>
                )
            }
        </GoogleMap>
    )
}

export default GoogleMaps;
